import { useQuery } from "@tanstack/react-query";
import React from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { NavLink, RouteComponentProps, withRouter } from "react-router-dom";

import { EditLocationPreferencesForm } from "./EditLocationPreferencesForm";
import { HealthIndexSection } from "./HealthIndexSection";
import { PageTitle } from "../../components/Common/PageTitle";
import { ScoreItem } from "../../components/ScoreItem";
import { useLocationPreferences } from "../../hooks/locationPreferences";
import { useMe } from "../../hooks/queries";
import { AccountType } from "../../models/AccountData";
import { LocationPreferences } from "../../models/LocationPreferences";
import DataSvc from "../../services/dataSvc";

import "./styles.scss";

interface MatchParams {
  locationId?: string;
  organizationId?: string;
}

type ILocationPreferencesPageProps = RouteComponentProps<MatchParams>;

const LocationPreferencesPage: React.FunctionComponent<
  ILocationPreferencesPageProps
> = (props) => {
  const locationId = props.match?.params?.locationId ?? "";

  const { t: _t } = useTranslation();
  const t = (key: string, options?: any) =>
    _t(`locationPreferencesPage.${key}`, options);

  const { data: me } = useMe();

  const navBackUrl = `/location/${locationId}${
    me?.organization_id ? `/organization/${me?.organization_id}` : ""
  }`;

  const { data: locationData } = useQuery(
    ["location", locationId],
    async () => {
      return await DataSvc.getLocation(locationId);
    }
  );

  const { data: locationPreferences, update } = useLocationPreferences(
    Number(locationId)
  );

  const isOrgAdmin = me?.type === AccountType.ORG_ADMIN;
  const hasVerifiedLocationAccount = me?.locations_accounts?.some(
    (la: any) =>
      Number(la.location_id) === Number(locationId) && la.verified === true
  );
  const isFormDisabled = isOrgAdmin && !hasVerifiedLocationAccount;

  const gbpLocIdNum =
    locationData?.gbp_location_id.replace("locations/", "") || undefined;

  return (
    <div className="right-content facility-review-summaries location-preferences-page">
      <div className="back-top-bar flex-grid">
        <NavLink to={navBackUrl} className="back-link">
          <i className="icons icon-back"></i>
          {t("back")}
        </NavLink>
        <div className="right-btns">
          {!!gbpLocIdNum && (
            <div className="to-gbp-button">
              <a
                href={`https://business.google.com/n/${gbpLocIdNum}/profile`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-blue"
                onClick={() => {
                  ReactGA.event({
                    category: "GBP Link",
                    action: "Click to GBP",
                    label: "main",
                  });
                }}
              >
                {t("to_facilitys_gbp_profile")}
              </a>
              <small className="footnote">{t("may_require_gbp_login")}</small>
            </div>
          )}
        </div>
      </div>
      <div className="top-title-bar">
        <PageTitle text={locationData?.name} />
        {locationData && (
          <div className="health-index-display">
            <span className="health-index-label">{t("health_index")}</span>
            <ScoreItem score={locationData.stat.score} />
          </div>
        )}
      </div>
      <div className="preferences-content">
        {locationData && locationPreferences && (
          <>
            <EditLocationPreferencesForm
              preferences={locationPreferences}
              submit={(data: LocationPreferences) => {
                return update(Number(locationId), data);
              }}
              disabled={isFormDisabled}
            />
            <HealthIndexSection locationId={Number(locationId)} />
          </>
        )}
      </div>
    </div>
  );
};

export default withRouter(LocationPreferencesPage);
